import React from 'react';

import { CUSTOMERIO_ANALYTICS_KEY } from '../constants/env';

const ensureCustomerIOGlobal = () => {
  const globalKeyName = 'cioanalytics';
  const win = window as typeof window & { [key: string]: any };

  if (win[globalKeyName] && win[globalKeyName].initialize) {
    win.cio = win[globalKeyName];
    return win[globalKeyName];
  }

  const analytics = (win[globalKeyName] = win[globalKeyName] || []);
  win.cioanalytics = analytics;
  win.cio = win.cioanalytics;

  if (analytics.invoked) {
    return analytics;
  }

  analytics.invoked = true;

  analytics.methods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'once',
    'off',
    'on',
    'addSourceMiddleware',
    'addIntegrationMiddleware',
    'setAnonymousId',
    'addDestinationMiddleware'
  ];

  analytics.factory = function (method: string) {
    return function (...args: any[]) {
      analytics.push([method, ...args]);
      return analytics;
    };
  };

  for (let i = 0; i < analytics.methods.length; i++) {
    const method = analytics.methods[i];
    analytics[method] = analytics.factory(method);
  }

  analytics.load = function (key: string, options?: Record<string, any>) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('data-global-customerio-analytics-key', globalKeyName);

    // Your alternative host (works only if cdp.customer.io is resolvable on your network)
    script.src =
      'https://cdp.customer.io/v1/analytics-js/snippet/' +
      key +
      '/analytics.min.js';

    script.onerror = function () {
      console.error('Failed to load Customer.io analytics script');
    };

    const first = document.getElementsByTagName('script')[0];
    first.parentNode?.insertBefore(script, first);
    (analytics as any)._writeKey = key;
    (analytics as any)._loadOptions = options;
  };

  (analytics as any).SNIPPET_VERSION = '4.15.3';

  return analytics;
};

const CustomerIOAnalytics: React.FC = () => {
  if (typeof window !== 'undefined') {
    ensureCustomerIOGlobal();
  }

  React.useEffect(() => {
    const analytics = ensureCustomerIOGlobal();

    if (!CUSTOMERIO_ANALYTICS_KEY) {
      console.warn(
        'Could not initialize Customer.io. CUSTOMERIO_ANALYTICS_KEY is undefined.'
      );
      return;
    }

    if ((analytics as any)._writeKey === CUSTOMERIO_ANALYTICS_KEY) {
      return;
    }

    (analytics as any).load(CUSTOMERIO_ANALYTICS_KEY, {
      integrations: {
        'Customer.io In-App Plugin': {
          anonymousInApp: true
        }
      }
    });
    (analytics as any).page();
  }, []);

  return null;
};

export default CustomerIOAnalytics;
